import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { AuthContext } from '../context/AuthContext';
import { apiGet, apiPost, apiPut, apiPatch, apiDelete } from '../services/api';
import { fmtDate as fmtUTC, fmtMin as fmtMinUTC, fmtSec as fmtSecUTC } from '../services/dates';
import LeadInstallerBox from './LeadInstallerBox';
import DocumentsBox from './DocumentsBox';
import './LeadsPage.css';

// ── Configuración ─────────────────────────────────────────────────────────────

const STATUSES = [
  { key: 'nuevo',       label: 'Nuevo lead',  color: 'blue'    },
  { key: 'contactado',  label: 'Contactado',  color: 'teal'    },
  { key: 'no_responde', label: 'No responde', color: 'gray'    },
  { key: 'seguimiento', label: 'Seguimiento', color: 'orange'  },
  { key: 'reservado',   label: 'Reservado',   color: 'purple'  },
  { key: 'primer_pago', label: 'Primer pago', color: 'indigo'  },
  { key: 'firmado',     label: 'Firmado',     color: 'emerald' },
  { key: 'perdido',     label: 'Perdido',     color: 'red'     },
];

const SOURCES = [
  { key: 'facebook_ads', label: 'Facebook Ads' },
  { key: 'google_ads',   label: 'Google Ads'   },
  { key: 'web',          label: 'Web'          },
  { key: 'whatsapp',     label: 'WhatsApp'     },
  { key: 'referido',     label: 'Referido'     },
  { key: 'manual',       label: 'Manual'       },
  { key: 'otro',         label: 'Otro'         },
];

// Formularios de la web (extra_data.web_form) — permiten distinguir en el listado
// por qué formulario entró un lead con origen "web"
const WEB_FORMS = [
  { key: 'calculadora', label: 'Calculadora', icon: '🧮' },
  { key: 'contacto',    label: 'Contacto',    icon: '📞' },
  { key: 'presupuesto', label: 'Presupuesto', icon: '📋' },
];

const STATUS_MAP   = Object.fromEntries(STATUSES.map(s => [s.key, s]));
const SOURCE_MAP   = Object.fromEntries(SOURCES.map(s => [s.key, s]));
const WEB_FORM_MAP = Object.fromEntries(WEB_FORMS.map(f => [f.key, f]));

const isAdmin = u => ['admin','superadmin'].includes(u?.role);
// fmt / fmtDt / fmtMin: timestamps auto-generados por BD (created_at, last_note_at,
// webEntryAt, etc.) — vienen en UTC y los mostramos en Europe/Madrid.
const fmt     = d  => fmtUTC(d);
const fmtDt   = d  => fmtSecUTC(d);   // con segundos
const fmtMin  = d  => fmtMinUTC(d);   // con minutos
// fmtDtLocal: para campos que el usuario escribe (due_date) — interpretamos como local.
const fmtDtLocal = d => d ? new Date(String(d).replace(' ','T')).toLocaleString('es-ES', { day:'2-digit', month:'2-digit', year:'2-digit', hour:'2-digit', minute:'2-digit', hour12:false }) : '—';
const fmtInput= d  => d ? new Date(d).toISOString().slice(0,16) : '';
const ini     = n  => (n||'?').split(' ').map(w=>w[0]).slice(0,2).join('').toUpperCase();

const PRIORITIES = [
  { key:'baja',  label:'Baja',  color:'gray'  },
  { key:'media', label:'Media', color:'orange'},
  { key:'alta',  label:'Alta',  color:'red'   },
];
const PRI_MAP = Object.fromEntries(PRIORITIES.map(p=>[p.key,p]));

export const DEFAULT_TEMPLATES = {
  whatsapp_no_quote: 'Hola {{nombre}}, me pongo en contacto contigo desde nuestra empresa. ¿Tienes un momento para hablar?',
  whatsapp_quote:    'Hola {{nombre}}, te enviamos el presupuesto que solicitaste. Cualquier duda estoy a tu disposición.',
  email_subject:     'Contacto - {{nombre}}',
  email_body:        'Hola {{nombre}},\n\nMe pongo en contacto contigo desde nuestra empresa.\n\nQuedo a tu disposición para cualquier consulta.\n\nSaludos',
};

function applyVars(tpl, lead) {
  const today = new Date().toLocaleDateString('es-ES', { day:'2-digit', month:'2-digit', year:'numeric' });
  return (tpl || '')
    .replace(/\{\{nombre\}\}/gi,    lead.name  || '')
    .replace(/\{\{Telefono\}\}/gi,  lead.phone || '')
    .replace(/\{\{poblacion\}\}/gi, lead.city  || '')
    .replace(/\{\{fecha\}\}/gi,     today);
}

// ── ContactActions ────────────────────────────────────────────────────────────

const IcoWA = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="13" height="13">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);
const IcoPhone = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.86 10.72a19.79 19.79 0 01-3.07-8.67A2 2 0 012.77 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 7.91a16 16 0 006.09 6.09l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
  </svg>
);
const IcoEmail = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
    <polyline points="22,6 12,13 2,6"/>
  </svg>
);

function ContactActions({ lead, token, templates, onStatusChanged, compact = false }) {
  const [waOpen, setWaOpen] = useState(false);
  const waRef = useRef(null);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    if (!waOpen) return;
    function handle(e) { if (waRef.current && !waRef.current.contains(e.target)) setWaOpen(false); }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [waOpen]);

  // Elige la plantilla según la campaña del lead; si no hay mensaje específico, usa el global.
  function resolveTpl(tplKey) {
    const camps = templates.whatsapp_campaigns;
    if (lead.campaignId && Array.isArray(camps)) {
      const m = camps.find(c => c && c.campaign && c.campaign === lead.campaignId);
      if (m && (m[tplKey] || '').trim()) return m[tplKey];
    }
    return templates[tplKey] || '';
  }

  async function openWA(tplKey) {
    setWaOpen(false);
    const phone = (lead.phone || '').replace(/\D/g, '');
    if (!phone) { alert('Este lead no tiene teléfono registrado'); return; }
    const msg = tplKey ? applyVars(resolveTpl(tplKey), lead) : '';
    window.open(`https://wa.me/${phone}${msg ? '?text=' + encodeURIComponent(msg) : ''}`, '_blank');
    if (lead.status === 'nuevo') {
      try { await apiPatch(`/leads/${lead.id}/status`, { status: 'contactado' }, token); onStatusChanged?.(); }
      catch {}
    }
  }

  function callLead(e) {
    e.stopPropagation();
    if (!lead.phone) { alert('Este lead no tiene teléfono registrado'); return; }
    window.location.href = `tel:${lead.phone}`;
  }

  function emailLead(e) {
    e.stopPropagation();
    if (!lead.email) { alert('Este lead no tiene email registrado'); return; }
    const subj = encodeURIComponent(applyVars(templates.email_subject || '', lead));
    const body = encodeURIComponent(applyVars(templates.email_body    || '', lead));
    window.location.href = `mailto:${lead.email}?subject=${subj}&body=${body}`;
  }

  if (compact) {
    return (
      <span className="lp-ci-wrap" onClick={e => e.stopPropagation()}>
        <button className="lp-ci lp-ci--wa"    onClick={e=>{e.stopPropagation();openWA('whatsapp_no_quote');}} title="WhatsApp"><IcoWA/></button>
        <button className="lp-ci lp-ci--call"  onClick={callLead}  title="Llamar"><IcoPhone/></button>
        <button className="lp-ci lp-ci--email" onClick={emailLead} title="Email"><IcoEmail/></button>
      </span>
    );
  }

  return (
    <div className="lp-contact-bar">
      {/* WhatsApp unificado con dropdown */}
      <div className="lp-wa-wrap" ref={waRef}>
        <button className="lp-cbar-btn lp-cbar-btn--wa" onClick={e => { e.stopPropagation(); setWaOpen(o => !o); }}>
          <IcoWA/> WhatsApp <span className="lp-wa-arrow">{waOpen ? '▲' : '▼'}</span>
        </button>
        {waOpen && (
          <div className="lp-wa-menu" onClick={e => e.stopPropagation()}>
            <button onClick={() => openWA('whatsapp_no_quote')}>Sin presupuesto</button>
            <button onClick={() => openWA('whatsapp_quote')}>Con presupuesto</button>
            <button onClick={() => openWA(null)}>Abrir chat libre</button>
          </div>
        )}
      </div>
      <button className="lp-cbar-btn lp-cbar-btn--call"  onClick={callLead}><IcoPhone/> Llamar</button>
      <button className="lp-cbar-btn lp-cbar-btn--email" onClick={emailLead}><IcoEmail/> Email</button>
    </div>
  );
}

// ── StatusBadge ───────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const s = STATUS_MAP[status] || { label: status, color: 'gray' };
  return <span className={`lp-badge lp-badge--${s.color}`}>{s.label}</span>;
}

// Badge del estado del bot de voz (columna "Calidad del lead"):
// En proceso (aún no llamado) · Intento X (llamado sin respuesta) · Contactado (respondió)
function BotBadge({ estado, intentos }) {
  let label = 'En proceso', color = 'blue';
  if (estado === 'contactado')      { label = 'Contactado';           color = 'teal';   }
  else if ((intentos || 0) > 0)     { label = `Intento ${intentos}`;  color = 'orange'; }
  return <span className={`lp-badge lp-badge--${color}`}>{label}</span>;
}

// ── Modal pérdida ─────────────────────────────────────────────────────────────

function LossModal({ onConfirm, onClose }) {
  const [reason, setReason] = useState('');
  const [error,  setError]  = useState('');
  const [saving, setSaving] = useState(false);

  async function submit() {
    if (reason.trim().length < 10) { setError(`Mínimo 10 caracteres. Llevas ${reason.trim().length}.`); return; }
    setSaving(true);
    try { await onConfirm(reason.trim()); onClose(); }
    catch (e) { setError(e.message); setSaving(false); }
  }

  return (
    <div className="lp-overlay" onClick={onClose}>
      <div className="lp-modal" onClick={e=>e.stopPropagation()}>
        <div className="lp-modal__head"><h3>Motivo de pérdida</h3><button className="lp-btn-x" onClick={onClose}>✕</button></div>
        <div className="lp-modal__body">
          <p className="lp-hint">Describe el motivo (mínimo 10 caracteres)</p>
          <textarea className="lp-textarea" rows={5} value={reason} onChange={e=>{setReason(e.target.value);setError('');}}
            placeholder="Explica detalladamente por qué se pierde este lead…" autoFocus />
          <div className="lp-char-count">{reason.trim().length} / 10 mín.</div>
          {error && <p className="lp-error">{error}</p>}
        </div>
        <div className="lp-modal__foot">
          <button className="lp-btn lp-btn--ghost" onClick={onClose}>Cancelar</button>
          <button className="lp-btn lp-btn--danger" onClick={submit} disabled={saving}>{saving?'…':'Marcar como perdido'}</button>
        </div>
      </div>
    </div>
  );
}

// ── NewLeadModal ──────────────────────────────────────────────────────────────

function NewLeadModal({ comerciales, onSave, onClose }) {
  const [f, setF] = useState({ name:'', phone:'', email:'', company:'', city:'', source:'manual', campaign_id:'', assigned_to:'' });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');
  const set = (k,v) => setF(p=>({...p,[k]:v}));

  async function submit(e) {
    e.preventDefault();
    if (!f.name.trim()) { setError('El nombre es obligatorio'); return; }
    setSaving(true);
    try { await onSave(f); onClose(); }
    catch (err) { setError(err.message); setSaving(false); }
  }

  return (
    <div className="lp-overlay" onClick={onClose}>
      <div className="lp-modal lp-modal--lg" onClick={e=>e.stopPropagation()}>
        <div className="lp-modal__head"><h3>Nuevo lead</h3><button className="lp-btn-x" onClick={onClose}>✕</button></div>
        <form onSubmit={submit} className="lp-modal__body lp-form">
          <div className="lp-form-2">
            <div className="lp-field"><label>Nombre *</label><input value={f.name} onChange={e=>set('name',e.target.value)} autoFocus/></div>
            <div className="lp-field"><label>Empresa</label><input value={f.company} onChange={e=>set('company',e.target.value)}/></div>
          </div>
          <div className="lp-form-2">
            <div className="lp-field"><label>Teléfono</label><input value={f.phone} onChange={e=>set('phone',e.target.value)}/></div>
            <div className="lp-field"><label>Email</label><input type="email" value={f.email} onChange={e=>set('email',e.target.value)}/></div>
          </div>
          <div className="lp-form-2">
            <div className="lp-field"><label>Ciudad</label><input value={f.city} onChange={e=>set('city',e.target.value)}/></div>
            <div className="lp-field"><label>Origen</label>
              <select value={f.source} onChange={e=>set('source',e.target.value)}>
                {SOURCES.map(s=><option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
          </div>
          <div className="lp-form-2">
            <div className="lp-field"><label>Código campaña</label><input value={f.campaign_id} onChange={e=>set('campaign_id',e.target.value)} placeholder="ID de campaña"/></div>
            {comerciales.length > 0 && (
              <div className="lp-field"><label>Asignar a</label>
                <select value={f.assigned_to} onChange={e=>set('assigned_to',e.target.value)}>
                  <option value="">Round-robin / auto</option>
                  {comerciales.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
            )}
          </div>
          {error && <p className="lp-error">{error}</p>}
          <div className="lp-modal__foot">
            <button type="button" className="lp-btn lp-btn--ghost" onClick={onClose}>Cancelar</button>
            <button type="submit" className="lp-btn lp-btn--primary" disabled={saving}>{saving?'Creando…':'Crear lead'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── BulkAssignModal ───────────────────────────────────────────────────────────

function BulkAssignModal({ comerciales, count, onConfirm, onClose }) {
  const [uid, setUid] = useState('');
  const [saving, setSaving] = useState(false);
  async function go() { if(!uid)return; setSaving(true); try{await onConfirm(uid);}catch{setSaving(false);} }
  return (
    <div className="lp-overlay" onClick={onClose}>
      <div className="lp-modal lp-modal--sm" onClick={e=>e.stopPropagation()}>
        <div className="lp-modal__head"><h3>Reasignar {count} lead{count!==1?'s':''}</h3><button className="lp-btn-x" onClick={onClose}>✕</button></div>
        <div className="lp-modal__body">
          <select className="lp-select" value={uid} onChange={e=>setUid(e.target.value)}>
            <option value="">Selecciona un comercial…</option>
            {comerciales.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <div className="lp-modal__foot">
          <button className="lp-btn lp-btn--ghost" onClick={onClose}>Cancelar</button>
          <button className="lp-btn lp-btn--primary" onClick={go} disabled={!uid||saving}>{saving?'…':'Asignar'}</button>
        </div>
      </div>
    </div>
  );
}

// ── BulkStatusModal ───────────────────────────────────────────────────────────

function BulkStatusModal({ count, onConfirm, onClose }) {
  const [status, setStatus] = useState('');
  const [saving, setSaving] = useState(false);
  async function go() { if(!status)return; setSaving(true); try{await onConfirm(status);}catch{setSaving(false);} }
  return (
    <div className="lp-overlay" onClick={onClose}>
      <div className="lp-modal lp-modal--sm" onClick={e=>e.stopPropagation()}>
        <div className="lp-modal__head"><h3>Cambiar estado — {count} lead{count!==1?'s':''}</h3><button className="lp-btn-x" onClick={onClose}>✕</button></div>
        <div className="lp-modal__body">
          <div className="lp-status-grid">
            {STATUSES.filter(s=>s.key!=='perdido').map(s=>(
              <button key={s.key} className={`lp-sopt lp-sopt--${s.color} ${status===s.key?'lp-sopt--sel':''}`} onClick={()=>setStatus(s.key)}>{s.label}</button>
            ))}
          </div>
        </div>
        <div className="lp-modal__foot">
          <button className="lp-btn lp-btn--ghost" onClick={onClose}>Cancelar</button>
          <button className="lp-btn lp-btn--primary" onClick={go} disabled={!status||saving}>{saving?'…':'Cambiar'}</button>
        </div>
      </div>
    </div>
  );
}

// ── LeadDrawer ────────────────────────────────────────────────────────────────

export function LeadDrawer({ lead, token, comerciales, templates, onClose, onUpdated, onGenerarPresupuesto }) {
  const { auth } = useContext(AuthContext);
  const drawerIsAdmin = ['admin', 'superadmin'].includes(auth.user?.role);
  const hasInstMod = auth.user?.role === 'superadmin' || (auth.modules || []).includes('instaladores');
  const [tab,        setTab]        = useState('info');
  const [editing,    setEditing]    = useState(false);
  const [form,       setForm]       = useState({});
  const [notes,      setNotes]      = useState([]);
  const [noteText,   setNoteText]   = useState('');
  const [lossModal,  setLossModal]  = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [noteSaving, setNoteSaving] = useState(false);
  // Tareas
  const [tasks,       setTasks]      = useState([]);
  const [taskForm,    setTaskForm]   = useState({ title:'', description:'', due_date:'', priority:'media', reminder:'' });
  const [taskSaving,  setTaskSaving] = useState(false);
  const [showTForm,   setShowTForm]  = useState(false);
  const [proformas,   setProformas]  = useState([]);
  const [loadingPro,  setLoadingPro] = useState(false);
  // Modal "Ganado"
  const [ganaModal,      setGanaModal]      = useState(false);
  const [ganaProformas,  setGanaProformas]  = useState([]);
  const [ganaLoading,    setGanaLoading]    = useState(false);
  const [ganaProformaId, setGanaProformaId] = useState('');
  const [ganaTransport,  setGanaTransport]  = useState('');

  // Flujo de pagos
  const [reservaModal,   setReservaModal]   = useState(false);
  const [resNombre,      setResNombre]      = useState('');
  const [resCantidad,    setResCantidad]    = useState('100');
  const [resSaving,      setResSaving]      = useState(false);

  const [pagoModal,      setPagoModal]      = useState(false);
  const [pagoTotal,      setPagoTotal]      = useState('');
  const [pagoPct,        setPagoPct]        = useState('70');
  const [pagoSaving,     setPagoSaving]     = useState(false);
  const [pagoProformas,  setPagoProformas]  = useState([]);

  const [firmadoModal,      setFirmadoModal]      = useState(false);
  const [firmadoSaving,     setFirmadoSaving]     = useState(false);
  const [firmadoProformaId, setFirmadoProformaId] = useState('');
  const [firmadoTransport,  setFirmadoTransport]  = useState('');

  // Aprobaciones del primer pago
  const [approvals, setApprovals] = useState([]);

  useEffect(() => {
    setForm({ name:lead.name, phone:lead.phone, email:lead.email, company:lead.company,
               city:lead.city, source:lead.source, campaign_id:lead.campaignId||'', assigned_to:lead.assignedTo||'' });
    setEditing(false);
    setProformas([]);
    loadNotes();
    loadTasks();
    apiGet(`/lead-approvals/by-lead/${lead.id}`, token).then(setApprovals).catch(() => setApprovals([]));
  }, [lead.id]);

  async function loadNotes() {
    try { const d = await apiGet(`/leads/${lead.id}`, token); setNotes(d.notes||[]); setProformas(d.proformas||[]); } catch {}
  }

  async function loadProformas() {
    setLoadingPro(true);
    try { const d = await apiGet(`/leads/${lead.id}`, token); setProformas(d.proformas||[]); }
    catch {} finally { setLoadingPro(false); }
  }

  async function deleteProforma(id) {
    if (!window.confirm('¿Eliminar este presupuesto?')) return;
    try {
      await apiDelete(`/proformas/${id}`, token);
      setProformas(p => p.filter(x => x.id !== id));
    } catch (e) { alert(e.message); }
  }

  async function downloadProforma(id) {
    try {
      const d = await apiGet(`/proformas/${id}`, token);
      // Inyectar botón de descarga PDF si no lo tiene
      let html = d.html || '';
      if (!html.includes('window.print()')) {
        const btn = `<div class="no-print" style="text-align:center;margin:20px 0"><button onclick="window.print()" style="padding:10px 30px;font-size:14px;font-weight:700;background:#E84300;color:#fff;border:none;border-radius:8px;cursor:pointer;font-family:inherit">Descargar PDF</button></div>`;
        html = html.replace('</body>', btn + '</body>');
        // Asegurar que .no-print se oculta al imprimir
        if (!html.includes('.no-print')) {
          html = html.replace('</style>', '.no-print{display:block}@media print{.no-print{display:none!important}}</style>');
        }
      }
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      const url  = URL.createObjectURL(blob);
      const tab  = window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      if (!tab) { const a = document.createElement('a'); a.href=url; a.download=(d.titulo||'presupuesto')+'.html'; document.body.appendChild(a); a.click(); document.body.removeChild(a); }
    } catch (e) { alert(e.message); }
  }

  async function loadTasks() {
    try { setTasks(await apiGet(`/tasks?lead_id=${lead.id}`, token)); } catch {}
  }

  async function addTask() {
    if (!taskForm.title.trim()) return;
    setTaskSaving(true);
    try {
      await apiPost('/tasks', { ...taskForm, lead_id: lead.id }, token);
      setTaskForm({ title:'', description:'', due_date:'', priority:'media', reminder:'' });
      setShowTForm(false);
      loadTasks();
    } catch (e) { alert(e.message); }
    finally { setTaskSaving(false); }
  }

  async function toggleTask(task) {
    const newStatus = task.status === 'completada' ? 'pendiente' : 'completada';
    try { await apiPut(`/tasks/${task.id}`, { status: newStatus }, token); loadTasks(); }
    catch (e) { alert(e.message); }
  }

  async function deleteTask(id) {
    if (!window.confirm('¿Eliminar esta tarea?')) return;
    await apiDelete(`/tasks/${id}`, token);
    setTasks(p => p.filter(t => t.id !== id));
  }

  const stf = (k,v) => setTaskForm(p=>({...p,[k]:v}));

  async function saveInfo() {
    setSaving(true);
    try {
      await apiPut(`/leads/${lead.id}`, form, token);
      if (form.assigned_to !== lead.assignedTo)
        await apiPost(`/leads/${lead.id}/assign`, { user_id: form.assigned_to }, token);
      onUpdated(); setEditing(false);
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  }

  async function changeStatus(newStatus, lossReason, acceptedProformaId, transportCost) {
    try {
      const body = { status: newStatus, loss_reason: lossReason };
      if (acceptedProformaId) {
        body.accepted_proforma_id = acceptedProformaId;
        body.transport_cost = transportCost || 0;
      }
      const res = await apiPatch(`/leads/${lead.id}/status`, body, token);
      onUpdated();
      if (newStatus === 'ganado' && res.clientId) alert('¡Lead ganado! Cliente creado automáticamente.');
    } catch (e) { alert(e.message); throw e; }
  }

  async function addNote() {
    if (!noteText.trim()) return;
    setNoteSaving(true);
    try { await apiPost(`/leads/${lead.id}/notes`, { content: noteText.trim() }, token); setNoteText(''); loadNotes(); }
    catch (e) { alert(e.message); }
    finally { setNoteSaving(false); }
  }

  async function deleteNote(id) {
    if (!window.confirm('¿Eliminar esta nota?')) return;
    await apiDelete(`/leads/notes/${id}`, token);
    setNotes(p => p.filter(n => n.id !== id));
  }

  const sf = (k,v) => setForm(p=>({...p,[k]:v}));

  return (
    <div className="lp-dback" onClick={onClose}>
      <aside className="lp-drawer" onClick={e=>e.stopPropagation()}>

        {/* Head */}
        <div className="lp-dr-head">
          <div className="lp-dr-headtop">
            <span className="lp-dr-code">{lead.leadCode||'—'}</span>
            <button className="lp-btn-x" onClick={onClose}>✕</button>
          </div>
          <div className="lp-dr-name">{lead.name||'Sin nombre'}</div>
          <div className="lp-dr-meta">
            <StatusBadge status={lead.status}/>
            {lead.assignedName && <span className="lp-dr-who">👤 {lead.assignedName}</span>}
            {lead.clientId && <span className="lp-dr-client">✓ Cliente creado</span>}
          </div>
          {approvals.length > 0 && (() => {
            const last = approvals[0]; // ordered DESC desde el backend
            if (last.status === 'pending') return (
              <div style={{marginTop:8,padding:'8px 10px',background:'#fef3c7',border:'1px solid #fde68a',borderRadius:6,fontSize:12,color:'#92400e'}}>
                ⏳ Primer pago pendiente de revisión del admin
              </div>
            );
            if (last.status === 'approved') return (
              <div style={{marginTop:8,padding:'8px 10px',background:'#dcfce7',border:'1px solid #bbf7d0',borderRadius:6,fontSize:12,color:'#166534'}}>
                ✓ Primer pago aprobado por {last.reviewedByName || 'admin'}{last.factoryEmailSentAt ? ' · email enviado a fábrica' : ''}
              </div>
            );
            if (last.status === 'rejected') return (
              <div style={{marginTop:8,padding:'8px 10px',background:'#fee2e2',border:'1px solid #fecaca',borderRadius:6,fontSize:12,color:'#991b1b'}}>
                ✕ Rechazado por {last.reviewedByName || 'admin'}: {last.rejectReason || '(sin motivo)'}
              </div>
            );
            return null;
          })()}
        </div>

        {/* Selector de estado */}
        <div className="lp-dr-states">
          {STATUSES.map(s => (
            <button
              key={s.key}
              className={`lp-ssb lp-ssb--${s.color} ${lead.status===s.key?'lp-ssb--cur':''}`}
              onClick={() => {
                if (s.key === lead.status) return;
                if (s.key === 'perdido') { setLossModal(true); return; }
                if (s.key === 'reservado') {
                  setResNombre(lead.name || ''); setResCantidad('100'); setReservaModal(true);
                  return;
                }
                if (s.key === 'primer_pago') {
                  setPagoTotal(''); setPagoPct('70'); setPagoProformas([]);
                  apiGet(`/proformas?lead_id=${lead.id}`, token).then(d => setPagoProformas(d)).catch(() => {});
                  setPagoModal(true);
                  return;
                }
                if (s.key === 'firmado') {
                  setFirmadoProformaId(''); setFirmadoTransport('');
                  setFirmadoModal(true);
                  setPagoProformas([]);
                  apiGet(`/proformas?lead_id=${lead.id}`, token).then(d => setPagoProformas(d)).catch(() => {});
                  return;
                }
                changeStatus(s.key);
              }}
            >{s.label}</button>
          ))}
        </div>

        {/* Acciones de contacto */}
        <div className="lp-dr-contact">
          <ContactActions lead={lead} token={token} templates={templates} onStatusChanged={onUpdated}/>
          {onGenerarPresupuesto && (
            <button className="lp-cbar-btn lp-cbar-btn--calc" onClick={() => onGenerarPresupuesto(lead)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                <path d="M9 7H6a2 2 0 00-2 2v9a2 2 0 002 2h12a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1-4h-4v4h4V3z"/>
              </svg>
              Generar presupuesto
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="lp-dr-tabs">
          <button className={`lp-dtab ${tab==='info'?'lp-dtab--on':''}`} onClick={()=>setTab('info')}>Información</button>
          <button className={`lp-dtab ${tab==='notas'?'lp-dtab--on':''}`} onClick={()=>setTab('notas')}>
            Notas {notes.length>0&&<span className="lp-dtab-n">{notes.length}</span>}
          </button>
          <button className={`lp-dtab ${tab==='tareas'?'lp-dtab--on':''}`} onClick={()=>setTab('tareas')}>
            Tareas {tasks.filter(t=>t.status==='pendiente').length>0&&<span className="lp-dtab-n lp-dtab-n--warn">{tasks.filter(t=>t.status==='pendiente').length}</span>}
          </button>
          <button className={`lp-dtab ${tab==='presupuestos'?'lp-dtab--on':''}`} onClick={()=>{ setTab('presupuestos'); loadProformas(); }}>
            Presupuestos {proformas.length>0&&<span className="lp-dtab-n">{proformas.length}</span>}
          </button>
          <button className={`lp-dtab ${tab==='documentos'?'lp-dtab--on':''}`} onClick={()=>setTab('documentos')}>
            Documentos
          </button>
        </div>

        <div className="lp-dr-body">

          {tab==='info' && (
            <div className="lp-dr-info">
              {!editing && hasInstMod && (
                <LeadInstallerBox lead={lead} token={token} isAdmin={drawerIsAdmin} onChanged={onUpdated} />
              )}
              {editing ? (
                <>
                  <div className="lp-field"><label>Nombre</label><input value={form.name} onChange={e=>sf('name',e.target.value)}/></div>
                  <div className="lp-form-2">
                    <div className="lp-field"><label>Teléfono</label><input value={form.phone} onChange={e=>sf('phone',e.target.value)}/></div>
                    <div className="lp-field"><label>Email</label><input value={form.email} onChange={e=>sf('email',e.target.value)}/></div>
                  </div>
                  <div className="lp-form-2">
                    <div className="lp-field"><label>Empresa</label><input value={form.company} onChange={e=>sf('company',e.target.value)}/></div>
                    <div className="lp-field"><label>Ciudad</label><input value={form.city} onChange={e=>sf('city',e.target.value)}/></div>
                  </div>
                  <div className="lp-form-2">
                    <div className="lp-field"><label>Origen</label>
                      <select value={form.source} onChange={e=>sf('source',e.target.value)}>
                        {SOURCES.map(s=><option key={s.key} value={s.key}>{s.label}</option>)}
                      </select>
                    </div>
                    <div className="lp-field"><label>Campaña</label><input value={form.campaign_id} onChange={e=>sf('campaign_id',e.target.value)}/></div>
                  </div>
                  {comerciales.length>0&&(
                    <div className="lp-field"><label>Comercial</label>
                      <select value={form.assigned_to} onChange={e=>sf('assigned_to',e.target.value)}>
                        <option value="">Sin asignar</option>
                        {comerciales.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
                      </select>
                    </div>
                  )}
                  <div className="lp-dr-edit-btns">
                    <button className="lp-btn lp-btn--ghost lp-btn--sm" onClick={()=>setEditing(false)}>Cancelar</button>
                    <button className="lp-btn lp-btn--primary lp-btn--sm" onClick={saveInfo} disabled={saving}>{saving?'…':'Guardar'}</button>
                  </div>
                </>
              ) : (
                <>
                  <button className="lp-btn lp-btn--ghost lp-btn--sm lp-dr-editbtn" onClick={()=>setEditing(true)}>Editar</button>
                  <dl className="lp-dl">
                    <dt>Código</dt>    <dd>{lead.leadCode||'—'}</dd>
                    <dt>Teléfono</dt>  <dd>{lead.phone||'—'}</dd>
                    <dt>Email</dt>     <dd>{lead.email||'—'}</dd>
                    <dt>Empresa</dt>   <dd>{lead.company||'—'}</dd>
                    <dt>Ciudad</dt>    <dd>{lead.city||'—'}</dd>
                    <dt>Origen</dt>
                    <dd>
                      <span>{SOURCE_MAP[lead.source]?.label||lead.source||'—'}</span>
                      {lead.sources && lead.sources.length > 1 && (
                        <span className="lp-multichannel">
                          {lead.sources.map(s => (
                            <span key={s} className={`lp-ch-badge ${s==='web'?'lp-ch-badge--web':''}`}>
                              {SOURCE_MAP[s]?.label || s}
                            </span>
                          ))}
                        </span>
                      )}
                    </dd>
                    {lead.webEntryAt && (
                      <><dt>Entrada web</dt><dd className="lp-web-entry">
                        🌐 {fmtMinUTC(lead.webEntryAt)}
                      </dd></>
                    )}
                    <dt>Campaña</dt>   <dd>{lead.campaignId||'—'}</dd>
                    <dt>Asignado</dt>  <dd>{lead.assignedName||'Sin asignar'}</dd>
                    <dt>Creado</dt>    <dd>{fmt(lead.createdAt)}</dd>
                    <dt>Actualizado</dt><dd>{fmt(lead.updatedAt)}</dd>
                    {lead.status==='perdido'&&lead.lossReason&&(
                      <><dt>Motivo pérdida</dt><dd className="lp-dl-loss">{lead.lossReason}</dd></>
                    )}
                  </dl>
                  {lead.extraData?.fb_fields && Object.keys(lead.extraData.fb_fields).length > 0 && (
                    <div className="lp-extra">
                      <div className="lp-extra__ttl">Respuestas del formulario (Facebook)</div>
                      {Object.entries(lead.extraData.fb_fields).map(([k, v]) => {
                        const label = k.replace(/[¿?]/g, '').replace(/_/g, ' ').trim();
                        const label2 = label.charAt(0).toUpperCase() + label.slice(1);
                        return (
                          <div key={k} className="lp-extra__item">
                            <span className="lp-extra__key">{label2}</span>
                            <span className="lp-extra__val">{String(v) || '—'}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {lead.extraData?.web_fields && Object.keys(lead.extraData.web_fields).length > 0 && (
                    <div className="lp-extra">
                      <div className="lp-extra__ttl">
                        {lead.extraData.web_form === 'calculadora' ? '🧮 Calculadora de la web'
                          : lead.extraData.web_form === 'presupuesto' ? '📋 Formulario de presupuesto (web)'
                          : lead.extraData.web_form === 'contacto' ? '📞 Formulario de contacto (web)'
                          : 'Solicitud desde la web'}
                      </div>
                      {Object.entries(lead.extraData.web_fields).map(([k, v]) => (
                        <div key={k} className="lp-extra__item">
                          <span className="lp-extra__key">{k}</span>
                          <span className="lp-extra__val" style={{ whiteSpace: 'pre-wrap' }}>{String(v) || '—'}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {lead.extraData && !lead.extraData.fb_fields && !lead.extraData.web_fields && Object.keys(lead.extraData).filter(k => k !== 'leadgen_id' && k !== 'web_form').length > 0 && (
                    <div className="lp-extra">
                      <div className="lp-extra__ttl">Datos del webhook</div>
                      {Object.entries(lead.extraData).filter(([k]) => k !== 'leadgen_id' && k !== 'web_form').map(([k, v]) => (
                        <div key={k} className="lp-extra__item">
                          <span className="lp-extra__key">{k === 'mensaje' ? 'Mensaje' : k}</span>
                          <span className="lp-extra__val" style={{ whiteSpace: 'pre-wrap', fontWeight: 500 }}>{String(v) || '—'}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {tab==='notas' && (
            <div className="lp-dr-notes">
              <div className="lp-notes-add">
                <textarea className="lp-textarea" rows={3} value={noteText} onChange={e=>setNoteText(e.target.value)}
                  placeholder="Escribe una nota interna… (Ctrl+Enter para enviar)"
                  onKeyDown={e=>{if(e.key==='Enter'&&e.ctrlKey)addNote();}} />
                <button className="lp-btn lp-btn--primary lp-btn--sm" onClick={addNote} disabled={noteSaving||!noteText.trim()}>
                  {noteSaving?'…':'Añadir nota'}
                </button>
              </div>
              <div className="lp-notes-list">
                {notes.length===0
                  ? <p className="lp-notes-empty">No hay notas todavía.</p>
                  : notes.map(n=>(
                    <div key={n.id} className="lp-note">
                      <div className="lp-note__hd">
                        <span className="lp-note__who">{n.user_name||'Usuario'}</span>
                        <span className="lp-note__dt">{fmtDt(n.created_at)}</span>
                        <button className="lp-note__del" onClick={()=>deleteNote(n.id)}>✕</button>
                      </div>
                      <p className="lp-note__txt">{n.content}</p>
                    </div>
                  ))
                }
              </div>
            </div>
          )}
          {tab==='tareas' && (
            <div className="lp-dr-tasks">
              <div className="lp-tasks-head">
                <button className="lp-btn lp-btn--primary lp-btn--sm" onClick={()=>setShowTForm(p=>!p)}>
                  {showTForm ? 'Cancelar' : '+ Nueva tarea'}
                </button>
              </div>

              {showTForm && (
                <div className="lp-task-form">
                  <div className="lp-field">
                    <label>Título *</label>
                    <input value={taskForm.title} onChange={e=>stf('title',e.target.value)} placeholder="¿Qué hay que hacer?" autoFocus/>
                  </div>
                  <div className="lp-field">
                    <label>Descripción</label>
                    <textarea className="lp-textarea" rows={2} value={taskForm.description} onChange={e=>stf('description',e.target.value)} placeholder="Detalles opcionales…"/>
                  </div>
                  <div className="lp-form-2">
                    <div className="lp-field">
                      <label>Fecha y hora</label>
                      <input type="datetime-local" value={taskForm.due_date} onChange={e=>stf('due_date',e.target.value)}/>
                    </div>
                    <div className="lp-field">
                      <label>Prioridad</label>
                      <select value={taskForm.priority} onChange={e=>stf('priority',e.target.value)}>
                        {PRIORITIES.map(p=><option key={p.key} value={p.key}>{p.label}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="lp-field">
                    <label>Aviso / Recordatorio</label>
                    <select value={taskForm.reminder} onChange={e=>stf('reminder',e.target.value)}>
                      <option value="">Sin aviso</option>
                      <option value="1d">1 día antes</option>
                      <option value="1h">1 hora antes</option>
                      <option value="30m">30 min antes</option>
                      <option value="15m">15 min antes</option>
                      <option value="0m">En el momento</option>
                    </select>
                  </div>
                  <div className="lp-task-form-btns">
                    <button className="lp-btn lp-btn--primary lp-btn--sm" onClick={addTask} disabled={taskSaving||!taskForm.title.trim()}>
                      {taskSaving?'…':'Guardar tarea'}
                    </button>
                  </div>
                </div>
              )}

              <div className="lp-tasks-list">
                {tasks.length===0
                  ? <p className="lp-notes-empty">No hay tareas todavía.</p>
                  : tasks.map(t=>(
                    <div key={t.id} className={`lp-task ${t.status==='completada'?'lp-task--done':''}`}>
                      <div className="lp-task__chk">
                        <input type="checkbox" checked={t.status==='completada'} onChange={()=>toggleTask(t)} title={t.status==='completada'?'Marcar pendiente':'Marcar completada'}/>
                      </div>
                      <div className="lp-task__body">
                        <div className="lp-task__title">{t.title}</div>
                        {t.description && <div className="lp-task__desc">{t.description}</div>}
                        <div className="lp-task__meta">
                          {t.dueDate && <span className="lp-task__due">📅 {fmtDtLocal(t.dueDate)}</span>}
                          <span className={`lp-task__pri lp-task__pri--${PRI_MAP[t.priority]?.color||'gray'}`}>{PRI_MAP[t.priority]?.label||t.priority}</span>
                          <span className="lp-task__by">{t.createdByName}</span>
                        </div>
                      </div>
                      <button className="lp-note__del" onClick={()=>deleteTask(t.id)} title="Eliminar">✕</button>
                    </div>
                  ))
                }
              </div>
            </div>
          )}

          {tab==='presupuestos' && (
            <div className="lp-dr-notes">
              {loadingPro ? (
                <p className="lp-notes-empty">Cargando…</p>
              ) : proformas.length === 0 ? (
                <p className="lp-notes-empty">No hay presupuestos guardados todavía.<br/>Usa la Calculadora para generar y guardar uno.</p>
              ) : (
                <div className="lp-proformas-list">
                  {proformas.map(p => (
                    <div key={p.id} className="lp-proforma-card">
                      <div className="lp-proforma-info">
                        <span className={`lp-proforma-tipo lp-proforma-tipo--${p.tipo}`}>
                          {p.tipo === 'reserva' ? '🔒 Reserva' : p.tipo === 'primer_pago' ? '💰 Paga y Señal' : p.tipo === 'contado' ? '📄 Contado' : '🏦 Financiado'}
                        </span>
                        <span className="lp-proforma-total">
                          {p.total ? Number(p.total).toLocaleString('es-ES',{minimumFractionDigits:2,maximumFractionDigits:2})+' €' : '—'}
                        </span>
                        <span className="lp-proforma-fecha">
                          {fmtMinUTC(p.created_at)}
                        </span>
                      </div>
                      <div style={{display:'flex',gap:'.4rem'}}>
                        <button className="lp-btn lp-btn--ghost lp-btn--sm" onClick={()=>downloadProforma(p.id)}>
                          ⬇ Ver PDF
                        </button>
                        <button className="lp-btn lp-btn--ghost lp-btn--sm" style={{color:'#dc2626',borderColor:'#fca5a5'}} onClick={()=>deleteProforma(p.id)} title="Eliminar presupuesto">
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab==='documentos' && (
            <div className="lp-dr-notes">
              <DocumentsBox entityType="lead" entityId={lead.id} token={token} />
            </div>
          )}

        </div>
      </aside>

      {lossModal&&(
        <LossModal onConfirm={r=>changeStatus('perdido',r)} onClose={()=>setLossModal(false)}/>
      )}

      {/* Modal Reserva */}
      {reservaModal&&(
        <div className="lp-overlay" onClick={()=>setReservaModal(false)}>
          <div className="lp-modal lp-modal--sm" onClick={e=>e.stopPropagation()}>
            <div className="lp-modal__head">
              <h3>Reserva realizada</h3>
              <button className="lp-btn-x" onClick={()=>setReservaModal(false)}>✕</button>
            </div>
            <div className="lp-modal__body">
              <div className="lp-field">
                <label>Nombre completo del cliente</label>
                <input value={resNombre} onChange={e=>setResNombre(e.target.value)}
                  placeholder={lead.name||'Nombre y apellidos'}/>
                <span style={{fontSize:'.75rem',color:'var(--muted)'}}>Puedes añadir apellidos. Si lo dejas vacío se usará el nombre del lead.</span>
              </div>
              <div className="lp-field" style={{marginTop:'.85rem'}}>
                <label>Cantidad de reserva (€)</label>
                <input type="number" min="0" step="0.01" value={resCantidad}
                  onChange={e=>setResCantidad(e.target.value)} placeholder="100"/>
              </div>
            </div>
            <div className="lp-modal__foot">
              <button className="lp-btn lp-btn--ghost" onClick={()=>setReservaModal(false)}>Cancelar</button>
              <button className="lp-btn lp-btn--primary" disabled={resSaving}
                onClick={async()=>{
                  setResSaving(true);
                  try {
                    const nombre = resNombre.trim() || lead.name || 'Cliente';
                    const amt = parseFloat(resCantidad)||100;
                    await apiPatch(`/leads/${lead.id}/status`, {
                      status:'reservado', reservation_name: nombre, reservation_amount: amt
                    }, token);
                    // Cargar datos frescos del tenant (por si se actualizaron después del login)
                    const settings = await apiGet('/settings', token);
                    const tc = settings.company_data || {};
                    const qf = tc.quote_footer || {};
                    const logo = settings.logo || auth.tenant?.logo || '';
                    const brand = (settings.colors?.brand) || auth.tenant?.colors?.brand || '#E84300';
                    const fecha = new Date().toLocaleDateString('es-ES',{day:'2-digit',month:'long',year:'numeric'});
                    const cuentaHTML = qf.cuenta_iban ? `<div class="banco"><p class="banco-title">Datos bancarios para la transferencia</p><p><strong style="font-family:monospace;font-size:15px;letter-spacing:.05em">${qf.cuenta_iban}</strong></p>${qf.cuenta_nombre ? `<p>A nombre de: <strong>${qf.cuenta_nombre}</strong></p>`:''}</div>` : '';
                    const html = `<!DOCTYPE html><html lang="es"><head><meta charset="UTF-8"><title>Reserva - ${nombre}</title>
<link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet">
<style>@page{size:A4;margin:0}*{box-sizing:border-box;margin:0;padding:0}body{font-family:'DM Sans',Arial,sans-serif;padding:30mm 25mm;color:#1a1a2e}.header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:30px;padding-bottom:20px;border-bottom:3px solid ${brand}}.logo img{max-height:60px}.empresa{text-align:right;font-size:11px;color:#555;line-height:1.6}.empresa strong{font-size:13px;color:#1a1a2e}h1{font-size:22px;color:${brand};margin:30px 0 20px;text-align:center}.datos{background:#f8f8f6;border-radius:10px;padding:24px;margin:20px 0}.datos p{font-size:14px;line-height:2;color:#333}.datos strong{color:#1a1a2e}.amount{text-align:center;margin:30px 0;padding:20px;background:${brand};border-radius:10px;color:#fff}.amount .label{font-size:12px;text-transform:uppercase;letter-spacing:.1em;opacity:.8}.amount .value{font-size:36px;font-weight:800;margin-top:4px}.banco{background:#EEF4FF;border-left:3px solid #003087;border-radius:8px;padding:18px 22px;margin:20px 0}.banco-title{font-size:13px;font-weight:700;color:#003087;margin-bottom:8px}.banco p{font-size:13px;line-height:1.8;color:#333;margin:0}.footer{margin-top:40px;text-align:center;font-size:11px;color:#888}@media print{.no-print{display:none!important}}</style></head><body>
<div class="header"><div class="logo">${logo?`<img src="${logo}" alt="Logo"/>` : `<strong style="font-size:18px;color:${brand}">${tc.nombre||auth.tenant?.name||''}</strong>`}</div><div class="empresa"><strong>${tc.nombre||auth.tenant?.name||''}</strong><br>${tc.nif?'NIF: '+tc.nif+'<br>':''}${tc.direccion||''} ${tc.cp||''} ${tc.ciudad||''}<br>${tc.telefono||''} ${tc.email?'· '+tc.email:''}</div></div>
<h1>Documento de reserva</h1>
<div class="datos"><p><strong>Cliente:</strong> ${nombre}</p><p><strong>Referencia:</strong> ${lead.leadCode||''}</p><p><strong>Fecha:</strong> ${fecha}</p><p><strong>Concepto:</strong> Reserva para visita técnica e inicio de proyecto</p></div>
<div class="amount"><div class="label">Cantidad entregada en concepto de reserva</div><div class="value">${amt.toLocaleString('es-ES',{minimumFractionDigits:2})} €</div></div>
${cuentaHTML}
<div class="datos"><p style="font-size:12px;color:#555">Esta cantidad será descontada del importe total del proyecto en el último pago. En caso de no realizarse la instalación por causas ajenas al cliente, el importe será íntegramente devuelto.</p></div>
<div class="footer">${tc.nombre||auth.tenant?.name||''} · ${tc.telefono||''} · ${tc.web||''}</div>
<div class="no-print" style="text-align:center;margin-top:30px"><button onclick="window.print()" style="padding:10px 30px;font-size:14px;font-weight:700;background:${brand};color:#fff;border:none;border-radius:8px;cursor:pointer">Descargar PDF</button></div></body></html>`;
                    await apiPost('/proformas', { lead_id: lead.id, tipo:'reserva', total: amt, titulo:`Reserva - ${nombre}`, html }, token);
                    const blob = new Blob([html], {type:'text/html;charset=utf-8'});
                    const url = URL.createObjectURL(blob);
                    window.open(url,'_blank');
                    setTimeout(()=>URL.revokeObjectURL(url),5000);
                    setReservaModal(false);
                    onUpdated();
                  } catch(e){ alert(e.message); }
                  finally { setResSaving(false); }
                }}>
                Confirmar reserva
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Primer Pago */}
      {pagoModal&&(
        <div className="lp-overlay" onClick={()=>setPagoModal(false)}>
          <div className="lp-modal lp-modal--sm" onClick={e=>e.stopPropagation()}>
            <div className="lp-modal__head">
              <h3>Primer pago</h3>
              <button className="lp-btn-x" onClick={()=>setPagoModal(false)}>✕</button>
            </div>
            <div className="lp-modal__body">
              <div className="lp-field">
                <label>Selecciona el presupuesto aceptado *</label>
                {pagoProformas.filter(p=>p.tipo!=='reserva'&&p.tipo!=='primer_pago').length === 0 ? (
                  <p style={{color:'#dc2626',fontSize:'.88rem',lineHeight:1.5,marginTop:8}}>
                    ⚠️ Este lead no tiene presupuestos.<br/>Genera uno desde la Calculadora primero.
                  </p>
                ) : (
                  <div style={{display:'flex',flexDirection:'column',gap:'.4rem',marginTop:'.4rem'}}>
                    {pagoProformas.filter(p=>p.tipo!=='reserva'&&p.tipo!=='primer_pago').map(p=>(
                      <label key={p.id} style={{display:'flex',alignItems:'center',gap:'.6rem',padding:'.45rem .6rem',border:'1px solid var(--border)',borderRadius:'8px',cursor:'pointer',background:pagoTotal===String(p.total)?'var(--brand-light, #eff6ff)':'transparent'}}>
                        <input type="radio" name="pago_prf" value={p.total}
                          checked={pagoTotal===String(p.total)}
                          onChange={()=>setPagoTotal(String(p.total||0))}/>
                        <span style={{fontSize:'.85rem'}}>
                          <strong>{p.titulo||'Presupuesto'}</strong> · {p.tipo} · <strong>{Number(p.total||0).toLocaleString('es-ES',{minimumFractionDigits:2})} €</strong>
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div className="lp-field" style={{marginTop:'.85rem'}}>
                <label>Porcentaje del primer pago (%)</label>
                <input type="number" min="0" max="100" step="1" value={pagoPct}
                  onChange={e=>setPagoPct(e.target.value)} placeholder="70"/>
              </div>
              {pagoTotal && (() => {
                const tot  = Number(pagoTotal) || 0;
                const pc   = Number(pagoPct) || 70;
                const fp   = Math.round(tot * pc) / 100;
                const res  = Number(lead.reservationAmount) || 0;
                const rest = Math.max(0, Math.round((tot - res - fp) * 100) / 100);
                return (
                  <div style={{background:'#f0fdf4',border:'1px solid #bbf7d0',borderRadius:10,padding:'14px 16px',marginTop:12}}>
                    <div style={{fontSize:'.82rem',color:'#555',lineHeight:1.8}}>
                      <div><strong>Total presupuesto:</strong> {tot.toLocaleString('es-ES',{minimumFractionDigits:2})} €</div>
                      {res > 0 && <div><strong>Reserva entregada:</strong> −{res.toLocaleString('es-ES',{minimumFractionDigits:2})} €</div>}
                      <div style={{fontSize:'.95rem',fontWeight:700,color:'#166534',borderTop:'1px solid #bbf7d0',paddingTop:6,marginTop:6}}>
                        Primer pago ({pc}%): {fp.toLocaleString('es-ES',{minimumFractionDigits:2})} €
                      </div>
                      <div style={{fontSize:'.85rem',color:'#374151',marginTop:4}}>
                        <strong>Pendiente día instalación:</strong> {rest.toLocaleString('es-ES',{minimumFractionDigits:2})} €
                      </div>
                    </div>
                    <div style={{fontSize:'.72rem',color:'#16a34a',marginTop:8,fontWeight:600}}>
                      📨 Al confirmar se enviará al admin para revisión.
                    </div>
                  </div>
                );
              })()}
            </div>
            <div className="lp-modal__foot">
              <button className="lp-btn lp-btn--ghost" onClick={()=>setPagoModal(false)}>Cancelar</button>
              <button className="lp-btn lp-btn--primary" disabled={pagoSaving||!pagoTotal}
                onClick={async()=>{
                  setPagoSaving(true);
                  try {
                    const total   = parseFloat(pagoTotal)||0;
                    const pct     = parseFloat(pagoPct)||70;
                    const pagoAmt = Math.round((total * pct / 100) * 100) / 100;
                    const resAmt  = Number(lead.reservationAmount) || 0;
                    const restAmt = Math.max(0, Math.round((total - resAmt - pagoAmt) * 100) / 100);
                    await apiPatch(`/leads/${lead.id}/status`, {
                      status:'primer_pago', budget_accepted: total, first_payment_pct: pct
                    }, token);
                    // Identificar la proforma aceptada (la que tiene este total)
                    const selectedProforma = pagoProformas.find(p => p.tipo !== 'reserva' && p.tipo !== 'primer_pago' && String(p.total) === pagoTotal);
                    // Crear solicitud de aprobación para el admin
                    try {
                      await apiPost('/lead-approvals', {
                        lead_id:            lead.id,
                        proforma_id:        selectedProforma?.id || null,
                        budget_total:       total,
                        reservation_amount: resAmt,
                        first_payment_pct:  pct,
                      }, token);
                    } catch (apErr) { console.warn('[approval] error:', apErr.message); }
                    const settings2 = await apiGet('/settings', token);
                    const tc = settings2.company_data || {};
                    const qf = tc.quote_footer || {};
                    const logo = settings2.logo || auth.tenant?.logo || '';
                    const brand = (settings2.colors?.brand) || auth.tenant?.colors?.brand || '#E84300';
                    const fecha = new Date().toLocaleDateString('es-ES',{day:'2-digit',month:'long',year:'numeric'});
                    const nombre = lead.reservationName || lead.name || 'Cliente';
                    const cuentaHTML = qf.cuenta_iban ? `<div class="banco"><p class="banco-title">Datos bancarios para la transferencia</p><p><strong style="font-family:monospace;font-size:15px;letter-spacing:.05em">${qf.cuenta_iban}</strong></p>${qf.cuenta_nombre?`<p>A nombre de: <strong>${qf.cuenta_nombre}</strong></p>`:''}</div>` : '';
                    const reservaLineaHTML = resAmt > 0 ? `<div class="linea"><span>Reserva entregada</span><strong style="color:#16a34a">−${resAmt.toLocaleString('es-ES',{minimumFractionDigits:2})} €</strong></div>` : '';
                    const restanteLineaHTML = `<div class="linea total" style="color:#374151;border-top:1px dashed #ccc"><span>Pendiente para el día de instalación</span><strong>${restAmt.toLocaleString('es-ES',{minimumFractionDigits:2})} €</strong></div>`;
                    const html = `<!DOCTYPE html><html lang="es"><head><meta charset="UTF-8"><title>Paga y Señal - ${nombre}</title>
<link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet">
<style>@page{size:A4;margin:0}*{box-sizing:border-box;margin:0;padding:0}body{font-family:'DM Sans',Arial,sans-serif;padding:30mm 25mm;color:#1a1a2e}.header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:30px;padding-bottom:20px;border-bottom:3px solid ${brand}}.logo img{max-height:60px}.empresa{text-align:right;font-size:11px;color:#555;line-height:1.6}.empresa strong{font-size:13px;color:#1a1a2e}h1{font-size:22px;color:${brand};margin:30px 0 20px;text-align:center}.datos{background:#f8f8f6;border-radius:10px;padding:24px;margin:20px 0}.datos p{font-size:14px;line-height:2;color:#333}.datos strong{color:#1a1a2e}.amount{text-align:center;margin:30px 0;padding:20px;background:${brand};border-radius:10px;color:#fff}.amount .label{font-size:12px;text-transform:uppercase;letter-spacing:.1em;opacity:.8}.amount .value{font-size:36px;font-weight:800;margin-top:4px}.resumen{background:#f8f8f6;border-radius:10px;padding:20px 24px;margin:20px 0;font-size:14px;line-height:2}.resumen .linea{display:flex;justify-content:space-between}.resumen .total{font-weight:800;font-size:16px;border-top:2px solid #ddd;padding-top:8px;margin-top:8px}.banco{background:#EEF4FF;border-left:3px solid #003087;border-radius:8px;padding:18px 22px;margin:20px 0}.banco-title{font-size:13px;font-weight:700;color:#003087;margin-bottom:8px}.banco p{font-size:13px;line-height:1.8;color:#333;margin:0}.footer{margin-top:40px;text-align:center;font-size:11px;color:#888}@media print{.no-print{display:none!important}}</style></head><body>
<div class="header"><div class="logo">${logo?`<img src="${logo}" alt="Logo"/>` : `<strong style="font-size:18px;color:${brand}">${tc.nombre||auth.tenant?.name||''}</strong>`}</div><div class="empresa"><strong>${tc.nombre||auth.tenant?.name||''}</strong><br>${tc.nif?'NIF: '+tc.nif+'<br>':''}${tc.direccion||''} ${tc.cp||''} ${tc.ciudad||''}<br>${tc.telefono||''} ${tc.email?'· '+tc.email:''}</div></div>
<h1>Justificante de Paga y Señal</h1>
<div class="datos"><p><strong>Cliente:</strong> ${nombre}</p><p><strong>Referencia:</strong> ${lead.leadCode||''}</p><p><strong>Fecha:</strong> ${fecha}</p></div>
<div class="resumen"><div class="linea"><span>Total presupuesto aceptado</span><strong>${total.toLocaleString('es-ES',{minimumFractionDigits:2})} €</strong></div>${reservaLineaHTML}<div class="linea total"><span>Primer pago (${pct}%)</span><strong style="color:${brand}">${pagoAmt.toLocaleString('es-ES',{minimumFractionDigits:2})} €</strong></div>${restanteLineaHTML}</div>
<div class="amount"><div class="label">Cantidad del primer pago</div><div class="value">${pagoAmt.toLocaleString('es-ES',{minimumFractionDigits:2})} €</div></div>
${cuentaHTML}
<div class="footer">${tc.nombre||auth.tenant?.name||''} · ${tc.telefono||''} · ${tc.web||''}</div>
<div class="no-print" style="text-align:center;margin-top:30px"><button onclick="window.print()" style="padding:10px 30px;font-size:14px;font-weight:700;background:${brand};color:#fff;border:none;border-radius:8px;cursor:pointer">Descargar PDF</button></div></body></html>`;
                    await apiPost('/proformas', { lead_id: lead.id, tipo:'primer_pago', total: pagoAmt, titulo:`Paga y Señal - ${nombre}`, html }, token);
                    const blob = new Blob([html], {type:'text/html;charset=utf-8'});
                    const url = URL.createObjectURL(blob);
                    window.open(url,'_blank');
                    setTimeout(()=>URL.revokeObjectURL(url),5000);
                    setPagoModal(false);
                    onUpdated();
                  } catch(e){ alert(e.message); }
                  finally { setPagoSaving(false); }
                }}>
                Confirmar primer pago
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Firmado */}
      {firmadoModal&&(()=>{
        const prfList = pagoProformas.filter(p=>p.tipo!=='reserva'&&p.tipo!=='primer_pago');
        const selPrf = prfList.find(p=>p.id===firmadoProformaId);
        const pTotal = selPrf ? Number(selPrf.total||0) : (lead.budgetAccepted||0);
        const resAmt = lead.reservationAmount||0;
        const fpAmt  = lead.firstPaymentAmount||0;
        const finalAmt = Math.max(0, pTotal - resAmt - fpAmt);
        return (
        <div className="lp-overlay" onClick={()=>setFirmadoModal(false)}>
          <div className="lp-modal lp-modal--sm" onClick={e=>e.stopPropagation()}>
            <div className="lp-modal__head">
              <h3>Pago final — Firmar</h3>
              <button className="lp-btn-x" onClick={()=>setFirmadoModal(false)}>✕</button>
            </div>
            <div className="lp-modal__body">
              {!lead.budgetAccepted && (
                <div className="lp-field">
                  <label>Selecciona el presupuesto aceptado *</label>
                  {prfList.length === 0 ? (
                    <p style={{color:'#dc2626',fontSize:'.88rem',marginTop:8}}>⚠️ No hay presupuestos. Genera uno desde la Calculadora.</p>
                  ) : (
                    <div style={{display:'flex',flexDirection:'column',gap:'.4rem',marginTop:'.4rem'}}>
                      {prfList.map(p=>(
                        <label key={p.id} style={{display:'flex',alignItems:'center',gap:'.6rem',padding:'.45rem .6rem',border:'1px solid var(--border)',borderRadius:'8px',cursor:'pointer',background:firmadoProformaId===p.id?'var(--brand-light, #eff6ff)':'transparent'}}>
                          <input type="radio" name="firmado_prf" value={p.id}
                            checked={firmadoProformaId===p.id}
                            onChange={()=>setFirmadoProformaId(p.id)}/>
                          <span style={{fontSize:'.85rem'}}>
                            <strong>{p.titulo||'Presupuesto'}</strong> · {p.tipo} · <strong>{Number(p.total||0).toLocaleString('es-ES',{minimumFractionDigits:2})} €</strong>
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                  <div className="lp-field" style={{marginTop:'.85rem'}}>
                    <label>Coste de transporte (€)</label>
                    <input type="number" min="0" step="0.01" value={firmadoTransport}
                      onChange={e=>setFirmadoTransport(e.target.value)} placeholder="0.00"/>
                  </div>
                </div>
              )}
              <div style={{background:'#f8f8f6',borderRadius:10,padding:'16px',lineHeight:2,fontSize:'.88rem',marginTop:12}}>
                <div><strong>Total presupuesto:</strong> {pTotal.toLocaleString('es-ES',{minimumFractionDigits:2})} €</div>
                {resAmt > 0 && <div><strong>Reserva entregada:</strong> −{resAmt.toLocaleString('es-ES',{minimumFractionDigits:2})} €</div>}
                {fpAmt > 0 && <div><strong>Primer pago ({lead.firstPaymentPct||70}%):</strong> −{fpAmt.toLocaleString('es-ES',{minimumFractionDigits:2})} €</div>}
                <div style={{fontSize:'1.05rem',fontWeight:800,color:'#166534',borderTop:'2px solid #ddd',paddingTop:8,marginTop:8}}>
                  Pago final pendiente: {finalAmt.toLocaleString('es-ES',{minimumFractionDigits:2})} €
                </div>
              </div>
            </div>
            <div className="lp-modal__foot">
              <button className="lp-btn lp-btn--ghost" onClick={()=>setFirmadoModal(false)}>Cancelar</button>
              <button className="lp-btn lp-btn--primary" disabled={firmadoSaving || (!lead.budgetAccepted && !firmadoProformaId)}
                onClick={async()=>{
                  setFirmadoSaving(true);
                  try {
                    const body = { status:'firmado' };
                    if (firmadoProformaId) {
                      body.accepted_proforma_id = firmadoProformaId;
                      body.transport_cost = parseFloat(firmadoTransport)||0;
                      body.budget_accepted = pTotal;
                    }
                    const res = await apiPatch(`/leads/${lead.id}/status`, body, token);
                    if (res.clientId) alert('¡Lead firmado! Cliente creado automáticamente.');
                    setFirmadoModal(false);
                    onUpdated();
                  } catch(e){ alert(e.message); }
                  finally { setFirmadoSaving(false); }
                }}>
                Confirmar firmado
              </button>
            </div>
          </div>
        </div>);
      })()}
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────

export default function LeadsPage({ onGenerarPresupuesto }) {
  const { auth }  = useContext(AuthContext);
  const token     = auth.token;
  const user      = auth.user;
  const admin     = isAdmin(user);
  const isSuperadmin = user?.role === 'superadmin';
  // La columna del bot de voz solo se muestra si el tenant tiene el módulo activo.
  const hasBotMod = isSuperadmin || (auth.modules || []).includes('bot_voz');

  const [leads,        setLeads]        = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [totalCount,   setTotalCount]   = useState(0);
  const [comerciales,  setComerciales]  = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [fStatus,      setFStatus]      = useState('');
  const [fUser,        setFUser]        = useState('');
  const [fSource,      setFSource]      = useState('');
  const [fCampaign,    setFCampaign]    = useState('');
  const [campaigns,    setCampaigns]    = useState([]);
  const [search,       setSearch]       = useState('');
  const [sortBy,       setSortBy]       = useState('createdAt');
  const [selected,     setSelected]     = useState(new Set());
  const [drawer,       setDrawer]       = useState(null);
  const [showNew,      setShowNew]      = useState(false);
  const [bulkModal,    setBulkModal]    = useState(null);
  const [templates,    setTemplates]    = useState(DEFAULT_TEMPLATES);
  const [tenants,      setTenants]      = useState([]);
  const [viewTenant,   setViewTenant]   = useState('');
  const [page,         setPage]         = useState(1);
  const [filteredTotal,setFilteredTotal]= useState(0);
  const PER_PAGE = 20;

  useEffect(() => {
    const url = isAdmin(user) ? '/settings' : '/users/me/templates';
    apiGet(url, token)
      .then(d => {
        const saved = isAdmin(user) ? (d.contact_templates || {}) : d;
        // Merge: saved keys override defaults, empty/missing keys fall back to defaults
        const merged = { ...DEFAULT_TEMPLATES };
        Object.entries(saved).forEach(([k, v]) => { if (v) merged[k] = v; });
        setTemplates(merged);
      })
      .catch(() => {});
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isSuperadmin) apiGet('/tenants', token).then(setTenants).catch(() => {});
  }, [isSuperadmin, token]);

  // Campañas/formularios disponibles para el filtro
  useEffect(() => {
    const p = new URLSearchParams();
    if (isSuperadmin && viewTenant) p.set('view_tenant_id', viewTenant);
    apiGet(`/leads/campaigns?${p}`, token).then(setCampaigns).catch(() => {});
  }, [token, isSuperadmin, viewTenant]);

  const loadStats = useCallback(async () => {
    try {
      const p = new URLSearchParams();
      if (isSuperadmin && viewTenant) p.set('view_tenant_id', viewTenant);
      const s = await apiGet(`/leads/stats?${p}`, token);
      const map = {};
      (s.byStatus || []).forEach(r => { map[r.status] = r.count; });
      setStatusCounts(map);
      setTotalCount(s.totals?.total || 0);
    } catch {}
  }, [token, isSuperadmin, viewTenant]);

  // Debounce del input de búsqueda para no disparar una petición por cada tecla
  const [searchDeb, setSearchDeb] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setSearchDeb(search), 350);
    return () => clearTimeout(id);
  }, [search]);

  const loadLeads = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (fStatus)   p.set('status',      fStatus);
      if (fUser)     p.set('assigned_to', fUser);
      if (fSource) {
        // "web:calculadora" → filtra por formulario web concreto
        if (fSource.startsWith('web:')) p.set('web_form', fSource.slice(4));
        else p.set('source', fSource);
      }
      if (fCampaign) p.set('campaign_id',  fCampaign);
      if (searchDeb) p.set('search',      searchDeb);
      if (isSuperadmin && viewTenant) p.set('view_tenant_id', viewTenant);
      p.set('page', String(page));
      p.set('per_page', String(PER_PAGE));
      const res = await apiGet(`/leads?${p}`, token);
      setLeads(res.data || []);
      setFilteredTotal(res.total || 0);
    } catch {} finally { setLoading(false); }
  }, [token, fStatus, fUser, fSource, fCampaign, searchDeb, isSuperadmin, viewTenant, page]);

  useEffect(() => { loadLeads(); loadStats(); }, [loadLeads, loadStats]);

  // Reset a página 1 cuando cambia cualquier filtro
  useEffect(() => { setPage(1); }, [fStatus, fUser, fSource, fCampaign, searchDeb, viewTenant]);

  useEffect(() => {
    if (admin) apiGet('/users/comerciales', token).then(setComerciales).catch(()=>{});
  }, []);

  // counts ya no se usa para los tabs (usamos statusCounts desde la API)
  const allSel     = leads.length>0 && selected.size===leads.length;
  const toggleAll  = () => setSelected(allSel ? new Set() : new Set(leads.map(l=>l.id)));
  const toggleOne  = id => setSelected(p=>{const s=new Set(p); s.has(id)?s.delete(id):s.add(id); return s;});

  async function handleCreate(form) { await apiPost('/leads',form,token); await loadLeads(); }

  async function handleBulkAssign(uid) {
    await apiPost('/leads/bulk-assign',{ids:[...selected],user_id:uid},token);
    setSelected(new Set()); setBulkModal(null); loadLeads();
  }
  async function handleBulkStatus(status) {
    await apiPost('/leads/bulk-status',{ids:[...selected],status},token);
    setSelected(new Set()); setBulkModal(null); loadLeads();
  }
  async function handleDelete(lead) {
    if (!window.confirm(`¿Eliminar "${lead.name}"?`)) return;
    await apiDelete(`/leads/${lead.id}`,token);
    setLeads(p=>p.filter(l=>l.id!==lead.id));
    if (drawer?.id===lead.id) setDrawer(null);
  }
  async function handleDrawerUpdate() {
    loadLeads(); loadStats();
    try { setDrawer(await apiGet(`/leads/${drawer.id}`,token)); } catch {}
  }

  return (
    <div className={`lp-page ${drawer?'lp-page--drawer':''}`}>
      <div className="lp-main">

        {/* Cabecera */}
        <div className="lp-header">
          <div><h2 className="lp-title">CRM Leads</h2><p className="lp-sub">{filteredTotal} lead{filteredTotal!==1?'s':''}</p></div>
          <button className="lp-btn lp-btn--primary" onClick={()=>setShowNew(true)}>+ Nuevo lead</button>
        </div>

        {/* Tabs estado */}
        <div className="lp-tabs">
          <button className={`lp-tab ${!fStatus?'lp-tab--on':''}`} onClick={()=>setFStatus('')}>
            Todos <span className="lp-tab-n">{totalCount}</span>
          </button>
          {STATUSES.map(s=>(
            <button key={s.key} className={`lp-tab lp-tab--${s.color} ${fStatus===s.key?'lp-tab--on':''}`}
              onClick={()=>setFStatus(fStatus===s.key?'':s.key)}>
              {s.label} {statusCounts[s.key]?<span className="lp-tab-n">{statusCounts[s.key]}</span>:null}
            </button>
          ))}
        </div>

        {/* Filtros */}
        <div className="lp-filters">
          {isSuperadmin && tenants.length > 0 && (
            <select className="lp-sel" value={viewTenant} onChange={e => { setViewTenant(e.target.value); setSelected(new Set()); }}>
              <option value="">Todas las empresas</option>
              {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          )}
          <input className="lp-search" placeholder="🔍  Nombre, teléfono, email, código…" value={search} onChange={e=>setSearch(e.target.value)}/>
          <select className="lp-sel" value={fSource} onChange={e=>setFSource(e.target.value)}>
            <option value="">Todos los orígenes</option>
            {SOURCES.flatMap(s => s.key === 'web'
              ? [
                  <option key="web" value="web">{s.label}</option>,
                  ...WEB_FORMS.map(f =>
                    <option key={`web:${f.key}`} value={`web:${f.key}`}>{'  '}Web · {f.label}</option>),
                ]
              : [<option key={s.key} value={s.key}>{s.label}</option>]
            )}
          </select>
          {campaigns.length>0 && (
            <select className="lp-sel" value={fCampaign} onChange={e=>setFCampaign(e.target.value)}>
              <option value="">Todas las campañas</option>
              {campaigns.map(c=><option key={c.campaign} value={c.campaign}>{c.campaign} ({c.count})</option>)}
            </select>
          )}
          {admin && comerciales.length>0 && (
            <select className="lp-sel" value={fUser} onChange={e=>setFUser(e.target.value)}>
              <option value="">Todos los comerciales</option>
              <option value="__unassigned__">Sin asignar</option>
              {comerciales.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          )}
          <select className="lp-sel lp-sel--sort" value={sortBy} onChange={e=>setSortBy(e.target.value)}>
            <option value="createdAt">Ordenar: Fecha entrada</option>
            <option value="lastNoteAt">Ordenar: Última nota</option>
          </select>
        </div>

        {/* Bulk bar */}
        {selected.size>0 && admin && (
          <div className="lp-bulk">
            <span className="lp-bulk-n">{selected.size} seleccionado{selected.size!==1?'s':''}</span>
            <button className="lp-btn lp-btn--ghost lp-btn--sm" onClick={()=>setBulkModal('assign')}>Reasignar</button>
            <button className="lp-btn lp-btn--ghost lp-btn--sm" onClick={()=>setBulkModal('status')}>Cambiar estado</button>
            <button className="lp-btn lp-btn--ghost lp-btn--sm" onClick={()=>setSelected(new Set())}>Cancelar</button>
          </div>
        )}

        {/* Tabla */}
        <div className="lp-twrap">
          {loading ? (
            <div className="lp-empty">Cargando leads…</div>
          ) : leads.length===0 ? (
            <div className="lp-empty">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
                <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>
              </svg>
              <p>No hay leads con los filtros aplicados.</p>
            </div>
          ) : (
            <table className="lp-table">
              <thead>
                <tr>
                  {admin&&<th className="lp-th-chk"><input type="checkbox" checked={allSel} onChange={toggleAll}/></th>}
                  <th>Código</th><th>Nombre</th><th>Teléfono</th>
                  <th>Origen</th><th>Estado</th>
                  {hasBotMod&&<th>Calidad del lead</th>}
                  {admin&&<th>Comercial</th>}
                  <th>Entrada</th><th>Última nota</th><th></th>
                </tr>
              </thead>
              <tbody>
                {[...leads].sort((a,b)=>{
                  if (sortBy === 'lastNoteAt') {
                    if (!a.lastNoteAt && !b.lastNoteAt) return 0;
                    if (!a.lastNoteAt) return 1;
                    if (!b.lastNoteAt) return -1;
                    return new Date(b.lastNoteAt) - new Date(a.lastNoteAt);
                  }
                  return new Date(b.createdAt) - new Date(a.createdAt);
                }).map(l=>(
                  <tr key={l.id} className={`lp-tr ${drawer?.id===l.id?'lp-tr--on':''}`} onClick={()=>setDrawer(l)}>
                    {admin&&(
                      <td className="lp-td-chk" onClick={e=>e.stopPropagation()}>
                        <input type="checkbox" checked={selected.has(l.id)} onChange={()=>toggleOne(l.id)}/>
                      </td>
                    )}
                    <td><span className="lp-code">{l.leadCode||'—'}</span></td>
                    <td>
                      <div className="lp-namecell">
                        <div className={`lp-av lp-av--${STATUS_MAP[l.status]?.color||'gray'}`}>{ini(l.name)}</div>
                        <span>{l.name||<span className="lp-muted">Sin nombre</span>}</span>
                      </div>
                    </td>
                    <td className="lp-muted">{l.phone||'—'}</td>
                    <td>
                      <span className="lp-src">{SOURCE_MAP[l.source]?.label||l.source||'—'}</span>
                      {l.extraData?.web_form ? (
                        <span className={`lp-form-badge lp-form-badge--${l.extraData.web_form}`}
                          title={l.webEntryAt ? `Contacto web: ${fmtMinUTC(l.webEntryAt)}` : undefined}>
                          {WEB_FORM_MAP[l.extraData.web_form]
                            ? `${WEB_FORM_MAP[l.extraData.web_form].icon} ${WEB_FORM_MAP[l.extraData.web_form].label}`
                            : l.extraData.web_form}
                        </span>
                      ) : l.webEntryAt ? (
                        <span className="lp-web-badge" title={`Contacto web: ${fmtMinUTC(l.webEntryAt)}`}>
                          WEB
                        </span>
                      ) : null}
                    </td>
                    <td><StatusBadge status={l.status}/></td>
                    {hasBotMod&&<td><BotBadge estado={l.botEstado} intentos={l.botIntentos}/></td>}
                    {admin&&<td className="lp-muted">{l.assignedName||<span className="lp-unassigned">Sin asignar</span>}</td>}
                    <td className="lp-muted lp-nw">{fmtMin(l.createdAt)}</td>
                    <td className="lp-muted lp-nw">{l.lastNoteAt ? fmtMin(l.lastNoteAt) : <span className="lp-muted">—</span>}</td>
                    <td className="lp-td-actions" onClick={e=>e.stopPropagation()}>
                      <ContactActions lead={l} token={token} templates={templates} onStatusChanged={loadLeads} compact/>
                      {admin&&(
                        <button className="lp-del" onClick={()=>handleDelete(l)} title="Eliminar">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6M10 11v6M14 11v6M9 6V4h6v2"/>
                          </svg>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Paginación */}
        {!loading && filteredTotal > PER_PAGE && (() => {
          const totalPages = Math.max(1, Math.ceil(filteredTotal / PER_PAGE));
          const curPage    = Math.min(page, totalPages);
          const from       = (curPage - 1) * PER_PAGE + 1;
          const to         = Math.min(curPage * PER_PAGE, filteredTotal);
          return (
            <div className="lp-pager">
              <span className="lp-pager-info">{from}–{to} de {filteredTotal}</span>
              <div className="lp-pager-btns">
                <button className="lp-btn lp-btn--ghost lp-btn--sm" disabled={curPage <= 1} onClick={() => setPage(1)}>« Primera</button>
                <button className="lp-btn lp-btn--ghost lp-btn--sm" disabled={curPage <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>‹ Anterior</button>
                <span className="lp-pager-cur">Página {curPage} / {totalPages}</span>
                <button className="lp-btn lp-btn--ghost lp-btn--sm" disabled={curPage >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Siguiente ›</button>
                <button className="lp-btn lp-btn--ghost lp-btn--sm" disabled={curPage >= totalPages} onClick={() => setPage(totalPages)}>Última »</button>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Drawer */}
      {drawer&&(
        <LeadDrawer lead={drawer} token={token} comerciales={comerciales} templates={templates}
          onClose={()=>setDrawer(null)} onUpdated={handleDrawerUpdate} onGenerarPresupuesto={onGenerarPresupuesto}/>
      )}

      {/* Modales */}
      {showNew&&<NewLeadModal comerciales={comerciales} onSave={handleCreate} onClose={()=>setShowNew(false)}/>}
      {bulkModal==='assign'&&<BulkAssignModal comerciales={comerciales} count={selected.size} onConfirm={handleBulkAssign} onClose={()=>setBulkModal(null)}/>}
      {bulkModal==='status'&&<BulkStatusModal count={selected.size} onConfirm={handleBulkStatus} onClose={()=>setBulkModal(null)}/>}
    </div>
  );
}
